import gsap from "gsap";
import { Map, VectorLayer, Label, Marker, TileLayer, ui } from "maptalks-gl";
import { capitalizeFirst } from "../../utils";
import { state } from "../state";
import { openModalTracked, closeModalTracked } from "../modalTracker";
import { getBackgroundPolygon, getRegions, getComuniData } from "./mapData";
import { MapRegion, MapComune } from "../../types";

export function destroyMap(): void {
  if (state.swiperInstance) {
    state.swiperInstance.destroy(true, true);
    state.swiperInstance = null;
  }
  if (state.map) {
    state.savedMapState = {
      center: state.map.getCenter(),
      zoom: state.map.getZoom(),
      pitch: state.map.getPitch(),
      bearing: state.map.getBearing(),
    };
    if (state.map._onGuitarSelect) {
      document.removeEventListener("guitar-select", state.map._onGuitarSelect);
    }
    if (state.map._updateLayerVisibility) {
      state.map.off("zoomend", state.map._updateLayerVisibility);
      state.map.off("moveend", state.map._updateLayerVisibility);
    }
    // Remove layers manually to avoid memory leaks
    try {
      state.map.getLayers().forEach((layer: any) => {
        if (typeof layer.clear === "function") {
          layer.clear();
        }
        layer.remove();
      });
    } catch (e) {
      console.warn("[destroyMap]: Error clearing layers", e);
    }
    state.map.remove();
    state.map = null;
  }
}

export const initHomeMap = (): void => {
  if (state.map) return;
  const mapContainer = document.getElementById("map");
  if (!mapContainer) return;

  // Initial fade
  gsap.set(mapContainer, { opacity: 0 });
  mapContainer.innerHTML = "";

  const viewConfig = state.savedMapState
    ? state.savedMapState
    : { center: [0, 0] as [number, number], zoom: 3 };

  state.map = new Map("map", {
    control: true,
    center: viewConfig.center,
    zoom: viewConfig.zoom,
    pitch: viewConfig.pitch || 0,
    bearing: viewConfig.bearing || 0,
    minZoom: 0,
    maxZoom: 10,
    attribution: false,
    baseLayer: new TileLayer("base", { renderer: "gl", opacity: 0 }),
  });

  setTimeout(() => {
    if (state.map) {
      state.map.checkSize();
      state.map.setView(viewConfig);
      // Fade in map
      gsap.to(mapContainer, {
        opacity: 1,
        duration: 1.2,
        ease: "power2.out",
        clearProps: "opacity",
      });
    }
  }, 200);

  const regionLayer = new VectorLayer("regionLayer").addTo(state.map);
  const cityLayer = new VectorLayer("cityLayer", { visible: false }).addTo(
    state.map,
  );
  const comuneLayer = new VectorLayer("comuneLayer", { visible: false }).addTo(
    state.map,
  );

  const regions = getRegions();
  const comuniData = getComuniData();
  const backgroundPolygon = getBackgroundPolygon();

  regionLayer.addGeometry(backgroundPolygon);

  let activeRegionId: number | null = null;

  function getRegionAtMapCenter(): number | null {
    const center = state.map.getCenter();
    for (const region of regions) {
      if (region.geometry.containsPoint(center)) return region.id;
    }
    return null;
  }

  function createComuneTooltip(comune: MapComune): any {
    if (!comune.tooltip) return null;
    let tooltipContent = comune.tooltip;
    if (comune.href)
      tooltipContent = `<a href="${comune.href}" class="tooltip-link" ${comune.target ? `target="${comune.target}"` : ""}>${comune.tooltip}</a>`;
    return new ui.ToolTip(tooltipContent, {
      showTimeout: 200,
      animation: "fade",
      animationDuration: 200,
      animationOnHide: true,
      dx: 0,
      dy: 5,
      cssName: "custom-tooltip",
    });
  }

  function addComuniForCity(cityId: string): void {
    const comuni = comuniData[cityId];
    for (const comune of comuni || []) {
      const handleComuneClick = () => {
        if (comune.href) {
          if (comune.target === "_blank")
            window.open(comune.href, "_blank", "noopener");
          else window.location.href = comune.href;
          return;
        }
        openModalTracked(comune.name, {
          section: "guitars",
          typology: "guitar",
        });
        state.map.animateTo(
          { center: comune.coordinates, zoom: 10 },
          { duration: 800, easing: "out" },
        );
      };

      if (comune.geometry) {
        comune.geometry.setSymbol({
          polygonFill: "#d1cfc1",
          polygonOpacity: 1,
          lineWidth: 10,
          lineColor: "#d1cfc1",
          lineOpacity: 0.5,
        });
        comune.geometry.setOptions({ cursor: "pointer" });
        if ((comune.geometry as any)._clickHandler) {
          comune.geometry.off("click", (comune.geometry as any)._clickHandler);
        }
        (comune.geometry as any)._clickHandler = handleComuneClick;
        comune.geometry.on("click", handleComuneClick);
        comuneLayer.addGeometry(comune.geometry);
      }

      const comuneMarker = new Marker(comune.coordinates, {
        symbol: {
          markerWidth: 6,
          markerHeight: 6,
          markerType: "ellipse",
          markerFill: "#333842",
          markerLineWidth: 0,
        },
      });
      const comuneNameLabel = new Label(
        capitalizeFirst(comune.name),
        comune.coordinates,
        {
          textSymbol: {
            textFaceName: "inter",
            textSize: 14,
            textFill: "#333842",
            textDy: 12,
          },
          cursor: "pointer",
          boxStyle: {
            padding: [15, 15],
            symbol: {
              markerFill: "#d8d6cb",
              markerLineWidth: 0,
              markerFillOpacity: 0.01,
            },
          },
        },
      );
      const tooltip = createComuneTooltip(comune);
      if (tooltip) tooltip.addTo(comuneNameLabel);

      comuneMarker.on("click", handleComuneClick);
      comuneNameLabel.on("click", handleComuneClick);
      comuneLayer.addGeometry(comuneMarker);
      comuneLayer.addGeometry(comuneNameLabel);
    }
  }

  function addComuniForRegion(region: MapRegion | undefined): void {
    if (!region) return;
    for (const city of region.cities || []) addComuniForCity(city.id);
  }

  function addCitiesForRegion(regionId: number): void {
    const region = regions.find((r) => r.id === regionId);
    if (!region) return;
    for (const city of region.cities || []) {
      const cityNameLabel = new Label(
        city.name.toUpperCase(),
        city.coordinates,
        {
          textSymbol: {
            textFaceName: "inter",
            textSize: 16,
            textFill: "#636363",
            textDy: 0,
            textWeight: "bold",
          },
          cursor: "pointer",
          boxStyle: {
            padding: [15, 15],
            symbol: {
              markerFill: "#d8d6cb",
              markerLineWidth: 0,
              markerFillOpacity: 0.01,
            },
          },
        },
      );
      cityNameLabel.on("click", () => {
        activeRegionId = regionId;
        openModalTracked(city.name, {
          section: region.section as any,
          typology: region.section === "guitars" ? "series" : null,
        });
        state.map.animateTo(
          { center: city.coordinates, zoom: 9 },
          { duration: 1000, easing: "out" },
        );
      });
      // Setup city polygon styling and add to layer
      if (city.geometry) {
        city.geometry.setSymbol({
          polygonFill: "#d8d6c9",
          polygonOpacity: 1,
          lineWidth: 10,
          lineColor: "#d8d6c9",
          lineOpacity: 0.5,
        });
        city.geometry.setOptions({ cursor: "pointer" });
        const handleCityClick = () => {
          activeRegionId = regionId;
          openModalTracked(city.name, {
            section: region.section as any,
            typology: region.section === "guitars" ? "series" : null,
          });
          state.map.animateTo(
            { center: city.coordinates, zoom: 9 },
            { duration: 1000, easing: "out" },
          );
        };
        if ((city.geometry as any)._clickHandler) {
          city.geometry.off("click", (city.geometry as any)._clickHandler);
        }
        (city.geometry as any)._clickHandler = handleCityClick;
        city.geometry.on("click", handleCityClick);
        cityLayer.addGeometry(city.geometry);
      }

      cityLayer.addGeometry(cityNameLabel);
    }
  }

  function updateLayerVisibility(): void {
    const currentZoom = state.map.getZoom();
    cityLayer.clear();
    comuneLayer.clear();
    cityLayer.hide();
    comuneLayer.hide();
    const regionAtCenter = getRegionAtMapCenter();
    if (regionAtCenter && activeRegionId !== regionAtCenter)
      activeRegionId = regionAtCenter;
    else if (!regionAtCenter && activeRegionId) activeRegionId = null;

    if (activeRegionId) {
      const region = regions.find((r) => r.id === activeRegionId);
      if (region) {
        const isContactsRegion = region.section === "contacts";
        if (currentZoom >= 5 && currentZoom < 8) {
          if (isContactsRegion) {
            addComuniForRegion(region);
            comuneLayer.show();
          } else {
            addCitiesForRegion(region.id);
            cityLayer.show();
          }
        } else if (currentZoom >= 8) {
          if (!isContactsRegion) {
            addCitiesForRegion(region.id);
            cityLayer.show();
          }
          addComuniForRegion(region);
          comuneLayer.show();
        }
      }
    }
  }

  regions.forEach((region) => {
    region.geometry.setId(region.id);
    region.geometry.setSymbol({
      polygonFill: "#e4e2d7",
      lineWidth: 5,
      lineColor: "#e4e2d7",
    });
    region.geometry._setOptions({ cursor: "default" });
    const regionNameLabel = new Label(region.name, region.center, {
      textSymbol: {
        textFaceName: "inter",
        textSize: 28,
        textFill: "#636363",
        textHaloFill: "#333",
        textHaloRadius: 0,
        textWeight: "200",
      },
      cursor: "pointer",
    });
    const handleRegionClick = () => {
      activeRegionId = region.id;

      // 1. First close the modal (slide out animation)
      if (state.isModalOpen) closeModalTracked();

      // 2. Reset history and the arrow
      state.lastModalArgs = null;
      const arrowBtn = document.querySelector(".arrow__btn");
      if (arrowBtn) arrowBtn.classList.remove("is-open");

      // 3. Hide everything completely after a short delay to show the movement
      gsap.to(".overlay__modal", {
        delay: 0.4, // Wait for the close start to provide smoothness
        opacity: 0,
        duration: 0.5,
        onComplete: () => {
          const modalEl = document.querySelector(
            ".overlay__modal",
          ) as HTMLElement | null;
          if (modalEl) modalEl.style.display = "none";
        },
      });

      state.map.animateTo(
        { center: region.center, zoom: 6 },
        { duration: 1000, easing: "out" },
      );
    };
    regionNameLabel.on("click", handleRegionClick);
    region.geometry.on("click", handleRegionClick);
    regionLayer.addGeometry(region.geometry);
    regionLayer.addGeometry(regionNameLabel);
  });

  state.map.on("zoomend", updateLayerVisibility);
  state.map.on("moveend", updateLayerVisibility);
  state.map._updateLayerVisibility = updateLayerVisibility;
  updateLayerVisibility();

  // Listen for guitar-select events from swiper slides in series modal
  const onGuitarSelect = (e: any) => {
    const guitarName = e.detail?.guitarName;
    if (!guitarName || !state.map) return;

    // Find the guitar's coordinates in comuniData
    let guitarCoordinates: [number, number] | null = null;
    for (const cityId of Object.keys(comuniData)) {
      const comuni = comuniData[cityId];
      const found = comuni.find(
        (c) => c.name.toLowerCase() === guitarName.toLowerCase(),
      );
      if (found) {
        guitarCoordinates = found.coordinates;
        break;
      }
    }

    // Center the map on the guitar
    if (guitarCoordinates) {
      state.map.animateTo(
        { center: guitarCoordinates, zoom: 10 },
        { duration: 800, easing: "out" },
      );
    }
  };

  document.addEventListener("guitar-select", onGuitarSelect);
  state.map._onGuitarSelect = onGuitarSelect;
};
